import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Loader2, ClipboardList, Bell, FolderClosed, Wallet, Archive, PauseCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Button } from "@/components/ui-kit/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui-kit/Card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Pill } from "@/components/ui-kit/Pill";
import { ClientFormDialog } from "@/components/clients/ClientFormDialog";
import { ClientActions } from "@/components/clients/ClientActions";
import { PaymentDialog } from "@/components/payments/PaymentDialog";
import { RetainerCard } from "@/components/payments/RetainerCard";
import { useFunder } from "@/funder/FunderContext";

const ALERT_TONE = { needs_review: "info", ready_to_invoice: "accent", unpaid: "muted" };

function MatterRow({ m, onOpen, closed }) {
  const { typeLabel } = useFunder();
  return (
    <TR data-testid={`matter-history-row-${m.id}`} className="cursor-pointer" onClick={() => onOpen(m)}>
      <TD className="font-medium text-foreground">{m.name}</TD>
      <TD className="text-muted-foreground">{typeLabel(m.type)}</TD>
      <TD><Pill tone={m.status === "Open" ? "success" : "muted"}>{m.status}</Pill></TD>
      <TD className="nums text-right">{money(m.balances?.client?.amount_due || 0)}</TD>
      <TD className="nums text-right">{money(m.balances?.acsa?.amount_due || 0)}</TD>
      <TD className="text-muted-foreground">{closed ? shortDate(m.close_date) : shortDate(m.open_date)}</TD>
    </TR>
  );
}

export function ClientOverviewPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { abbr } = useFunder();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const load = () =>
    api.get(`/clients/${clientId}/overview`).then((r) => setData(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => {
    load();
  }, [clientId]);

  const client = data?.client;
  const active = data?.active_matters || [];
  const closed = data?.closed_matters || [];
  const alerts = data?.alerts || [];
  const primaryActive = active[0];
  const isArchived = data?.is_archived;
  const isOnHold = data?.is_on_hold;
  const outstanding = [...active, ...closed].reduce(
    (s, m) => s + (m.balances?.client?.amount_due || 0) + (m.balances?.acsa?.amount_due || 0),
    0
  );
  const openMatter = (m) => navigate(m.status === "Open" ? `/workbench/${m.id}` : `/matters/${m.id}/record`);

  return (
    <div className="space-y-6" data-testid="client-overview-page">
      <Link to="/clients-matters" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-clients">
        <ArrowLeft className="h-4 w-4" /> All clients
      </Link>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!data ? (
        <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">{client.name}</h1>
                {isArchived && <Pill tone="muted" data-testid="client-archived-pill">Archived</Pill>}
                {isOnHold && !isArchived && <Pill tone="accent" data-testid="client-onhold-pill">On Hold</Pill>}
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">{[client.email, client.phone].filter(Boolean).join(" · ") || "No contact details"}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setEditOpen(true)} data-testid="edit-client-button">
                <Pencil className="h-4 w-4" /> Edit client
              </Button>
              <Button variant="secondary" onClick={() => setPayOpen(true)} data-testid="overview-record-payment-button">
                <Wallet className="h-4 w-4" /> Record payment
              </Button>
              {!isArchived && (
                <Button onClick={() => navigate(`/clients-matters/${clientId}/matters/new`)} data-testid="new-matter-button">
                  <Plus className="h-4 w-4" /> New matter
                </Button>
              )}
              {!isArchived && primaryActive && (
                <Button onClick={() => navigate(`/workbench/${primaryActive.id}`)} data-testid={`open-active-matter-${primaryActive.id}`}>
                  <ClipboardList className="h-4 w-4" /> Open active matter
                </Button>
              )}
              <ClientActions
                client={client}
                canHardDelete={data.can_hard_delete}
                outstanding={outstanding}
                openMatterCount={active.length}
                variant="header"
                onChanged={load}
                afterDelete={() => navigate("/clients-matters")}
              />
            </div>
          </div>

          {isOnHold && !isArchived && (
            <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm" data-testid="onhold-banner">
              <PauseCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span className="text-foreground">This client is on hold — parked out of the active list and dashboard work alerts, but their matter stays open and fully billable and collectible. Set them active again anytime.</span>
            </div>
          )}

          {isArchived && (
            <div className="flex items-start gap-2 rounded-lg border border-status-superseded/30 bg-status-superseded/10 px-4 py-3 text-sm" data-testid="archived-banner">
              <Archive className="mt-0.5 h-4 w-4 shrink-0 text-status-superseded" />
              <span className="text-foreground">This client is archived — their matters are closed and they&apos;re excluded from the active list and dashboard. You can still record a final payment here, or reinstate them.</span>
            </div>
          )}

          {alerts.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center gap-2">
                <Bell className="h-4 w-4 text-status-sent" />
                <CardTitle>Notifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {alerts.map((a, i) => (
                  <div key={i} data-testid={`overview-alert-${i}`} className="flex items-center gap-2 text-sm">
                    <Pill tone={ALERT_TONE[a.type] || "neutral"}>{a.type.replace(/_/g, " ")}</Pill>
                    <span className="text-foreground">{a.message}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>Active matter</CardTitle></CardHeader>
            <CardContent>
              {active.length === 0 ? (
                <p className="py-4 text-sm text-muted-foreground">No active matter. Create one to start logging time.</p>
              ) : (
                <Table>
                  <THead><TR><TH>Matter</TH><TH>Type</TH><TH>Status</TH><TH className="text-right">Client due</TH><TH className="text-right">{abbr} due</TH><TH>Opened</TH></TR></THead>
                  <TBody>{active.map((m) => <MatterRow key={m.id} m={m} onOpen={openMatter} />)}</TBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <RetainerCard clientId={clientId} />

          {closed.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center gap-2">
                <FolderClosed className="h-4 w-4 text-muted-foreground" />
                <CardTitle>Closed matter history</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <THead><TR><TH>Matter</TH><TH>Type</TH><TH>Status</TH><TH className="text-right">Client due</TH><TH className="text-right">{abbr} due</TH><TH>Closed</TH></TR></THead>
                  <TBody>{closed.map((m) => <MatterRow key={m.id} m={m} onOpen={openMatter} closed />)}</TBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} onSaved={load} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} clientId={clientId} onDone={load} />
    </div>
  );
}
